import React, { useState, useRef, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { BellIcon } from "./SvgIcons";
import CurrentOperations from "../views/CurrentOperations";

/**
 * A single notification row.
 *
 * Props:
 *   notification – { id, type ('info'|'success'|'warning'|'error'), title, message, time, unread? }
 */
function NotificationItem({ notification }) {
	const { type = "info", title, message, time, unread } = notification;
	return (
		<div className={`nc-item nc-item--${type}${unread ? " nc-item--unread" : ""}`}>
			<span className="nc-item__dot" />
			<div className="nc-item__body">
				{title && <div className="nc-item__title">{title}</div>}
				{message && <div className="nc-item__message">{message}</div>}
			</div>
			{time && <div className="nc-item__time">{time}</div>}
		</div>
	);
}

/**
 * Notification center for the top header.
 *
 * Props:
 *   notifications – array of notification objects to render
 *
 * Each notification: { id, type ('info'|'success'|'warning'|'error'), title, message, time, unread? }
 *
 * CurrentOperations is always mounted so polling continues regardless of
 * whether the dropdown is open or closed.
 */
export function NotificationCenter({ notifications = [], org }) {
	const [open, setOpen] = useState(false);
	const [taskCount, setTaskCount] = useState(0);
	const [stripeCount, setStripeCount] = useState(0);
	const wrapRef = useRef(null);

	// Reset state when org changes
	useEffect(() => {
		setOpen(false);
		setTaskCount(0);
		setStripeCount(0);
	}, [org]);

	// Close dropdown on outside click
	useEffect(() => {
		const handler = (e) => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	const handleTaskCountChange = useCallback((count) => {
		setTaskCount(count);
	}, []);

	const handleStripeVisible = useCallback((visible) => {
		setStripeCount(visible ? 1 : 0);
	}, []);

	const totalCount = notifications.length + taskCount + stripeCount;

	return (
		<div className="th-dropdown-wrap" ref={wrapRef}>
			<button
				className="th-icon-btn"
				onClick={() => setOpen((v) => !v)}
				aria-label="Notifications"
				aria-expanded={open}>
				<BellIcon size={24} />
				{totalCount > 0 && (
					<span className="th-badge">{totalCount > 99 ? "99+" : totalCount}</span>
				)}
			</button>

			<div className={`th-dropdown th-dropdown--notifications${open ? " th-dropdown--open" : ""}`}>
				<div className="th-dropdown-header">
					<span>Notifications</span>
					{totalCount > 0 && (
						<span className="th-dropdown-badge">{totalCount}</span>
					)}
				</div>

				{/* Always mounted so CurrentOperations can poll and report task count */}
				<div className="nc-body">
					{notifications.length > 0 && (
						<ul className="nc-list">
							{notifications.map((n) => (
								<li key={n.id}>
									<NotificationItem notification={n} />
								</li>
							))}
						</ul>
					)}

					<CurrentOperations
						renderMode="notification"
						onTaskCountChange={handleTaskCountChange}
						onClose={() => setOpen(false)}
					/>

					{totalCount === 0 && (
						<div className="nc-empty">No notifications</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default connect((state) => ({ org: state.roleManager.org }))(NotificationCenter);
